import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Collapse, Grid, Stack, Button, ToggleButton } from '@mui/material'
import { CropInfoCard } from '../crop/crop-list/crop-info-card'
import { OrgBreadcrumbs } from './org-breadcrumbs'
import { useOrganizations } from '../common/use-organizations'
import _ from 'lodash'
import { CropScheduleCard } from '~/crop/crop-schedule/crop-schedule-card'
import { setActiveOrgs, useOrgContextDispatch } from '../common/context/organization/org-context'
import { useOrgParams } from '~/common/use-org-params'
import {
  APPBAR_HEIGHT_REM,
  DASHBOARD_GRID_SPACING_REM,
  DASHBOARD_TITLE_HEIGHT_REM,
  APPBAR_EXTRA_DESKTOP_REM,
  CONTENT_PADDING_RBL_REM,
} from '~/common/constants/layout-constants'
import { useFeatures } from '~/common/use-features'
import { TaskListCard } from '~/tasks/task-list-card'
import { AddMenu } from './add-menu'
import { MapCard } from '~/map/map-card'
import { MapRefreshProvider } from '~/map/context/map-refresh-context'
import { ReportMenu } from './report-menu'
import { DashboardCardsMenu } from './dashboard-cards-menu'
import { MetricsCard } from '~/metrics/metrics-card'

// HALF CONTENT
const HALF_CONTENT_HEIGHT_XS = `calc(\
(100vh\
  - ${APPBAR_HEIGHT_REM}\
  - ${DASHBOARD_TITLE_HEIGHT_REM}\
  - ${DASHBOARD_GRID_SPACING_REM}\
  - ${CONTENT_PADDING_RBL_REM}\
) / 2)`
const HALF_CONTENT_HEIGHT_MD = `calc(\
  (100vh\
    - ${APPBAR_HEIGHT_REM}\
    - ${APPBAR_EXTRA_DESKTOP_REM}\
    - ${DASHBOARD_TITLE_HEIGHT_REM}\
    - ${DASHBOARD_GRID_SPACING_REM}\
    - ${CONTENT_PADDING_RBL_REM}\
  ) / 2)`

// FULL CONTENT
const FULL_CONTENT_HEIGHT_XS = `calc(\
  100vh\
    - ${APPBAR_HEIGHT_REM}\
    - ${DASHBOARD_TITLE_HEIGHT_REM}\
    - ${CONTENT_PADDING_RBL_REM}\
  )`
const FULL_CONTENT_HEIGHT_MD = `calc(\
  100vh\
    - ${APPBAR_HEIGHT_REM}\
    - ${APPBAR_EXTRA_DESKTOP_REM}\
    - ${DASHBOARD_TITLE_HEIGHT_REM}\
    - ${CONTENT_PADDING_RBL_REM}\
  )`

// Right column split heights when metrics card is visible (md+ only)
const SCHEDULE_WITH_METRICS_HEIGHT_MD = `calc(
  (${FULL_CONTENT_HEIGHT_MD} - ${DASHBOARD_GRID_SPACING_REM}) * 0.7
)`
const METRICS_HEIGHT_MD = `calc(
  (${FULL_CONTENT_HEIGHT_MD} - ${DASHBOARD_GRID_SPACING_REM}) * 0.3
)`

/**
 * Unified Dashboard component
 * Handles both farm view (showing plots) and plot view (showing patches)
 * Conditionally renders UI based on query params (farmId, plotId, patchId)
 */
function Dashboard() {
  const dispatch = useOrgContextDispatch()
  const { farmId, plotId, patchId } = useOrgParams()
  const { features } = useFeatures()

  // Column-level visibility
  const hasMapCard = features['map_card']
  const hasBottomLeftCard = features['crop_info_card'] || features['task_list_card']
  const hasLeftColumn = hasMapCard || hasBottomLeftCard
  const hasTimelineCard = features['timeline_card']
  const hasMetricsCard = features['metrics_card']
  const hasRightColumn = hasTimelineCard || hasMetricsCard

  // Column sizes expand to fill when the other column is empty
  const leftColSize = hasRightColumn ? { md: 4, lg: 3 } : { xs: 12 }
  const rightColSize = hasLeftColumn ? { xs: 12, md: 8, lg: 9 } : { xs: 12 }

  // Right column card heights — each fills fully when the other is absent
  const timelineHeight = {
    xs: FULL_CONTENT_HEIGHT_XS,
    md: hasMetricsCard ? SCHEDULE_WITH_METRICS_HEIGHT_MD : FULL_CONTENT_HEIGHT_MD,
  }
  const metricsHeight = {
    md: hasTimelineCard ? METRICS_HEIGHT_MD : FULL_CONTENT_HEIGHT_MD,
  }

  // Dynamic square aspect ratio for map card
  const leftGridRef = useRef(null)
  const [mapCardHeight, setMapCardHeight] = useState(0)

  // Dynamic height for remaining vertical space (task_list_card or crop_info_card)
  // When map card is absent the bottom card fills the full column height
  const remainingCardHeight = useMemo(() => {
    if (!hasMapCard) {
      return { xs: FULL_CONTENT_HEIGHT_XS, md: FULL_CONTENT_HEIGHT_MD }
    }
    if (mapCardHeight === 0) return 'auto'
    return {
      xs: `calc(${FULL_CONTENT_HEIGHT_XS} - ${mapCardHeight}px - ${DASHBOARD_GRID_SPACING_REM})`,
      md: `calc(${FULL_CONTENT_HEIGHT_MD} - ${mapCardHeight}px - ${DASHBOARD_GRID_SPACING_REM})`
    }
  }, [hasMapCard, mapCardHeight])

  // Conditional organization fetching - always use useOrganizations for consistency
  const ids = useMemo(() => {
    if (plotId) {
      return _.compact([farmId, plotId, patchId])
    }
    return _.compact([farmId])
  }, [farmId, plotId, patchId])

  const typeValidations = useMemo(() => {
    if (plotId) {
      return patchId ? ['farm', 'plot', 'patch'] : ['farm', 'plot']
    }
    return ['farm']
  }, [plotId, patchId])

  const { data: orderedOrgs } = useOrganizations({ ids, typeValidations })
  const [farm, plot, patch] = orderedOrgs

  // fetch once, to avoid many supabase calls that require their data
  useEffect(() => {
    dispatch(setActiveOrgs({ activeOrgs: orderedOrgs }))
  }, [dispatch, farm, orderedOrgs, patch, plot])

  // Measure map card width and set height to maintain square aspect ratio
  // Depends on hasMapCard: the Grid is conditionally rendered, so leftGridRef.current
  // is null during the initial render while user data is loading. Re-running when
  // hasMapCard changes ensures the observer is set up once the Grid mounts.
  useEffect(() => {
    if (!hasMapCard) return
    const mapCardElement = leftGridRef.current
    if (!mapCardElement) return

    let isMounted = true

    const resizeObserver = new ResizeObserver((entries) => {
      // Guard against state updates after unmount (HMR safety)
      if (!isMounted) return

      for (const entry of entries) {
        const width = entry.contentRect.width
        // Use requestAnimationFrame to defer state update
        requestAnimationFrame(() => {
          if (isMounted) {
            setMapCardHeight(width)
          }
        })
      }
    })

    resizeObserver.observe(mapCardElement)

    return () => {
      isMounted = false
      resizeObserver.disconnect()
    }
  }, [hasMapCard])

  return (
    <MapRefreshProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            height: DASHBOARD_TITLE_HEIGHT_REM,
            mb: '0.25rem',
          }}
        >
          <OrgBreadcrumbs />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {features['customize_dashbaord'] && <DashboardCardsMenu />}
            {features['add_reports'] && <ReportMenu />}
            {/* If not all are false, show it */}
            {!(
              !features['add_crops'] &&
              !features['add_plots'] &&
              !features['add_tasks']
            ) && <AddMenu />}
          </Box>
        </Box>

        <Grid container spacing={DASHBOARD_GRID_SPACING_REM}>
          {hasLeftColumn && (
            <Grid ref={leftGridRef} size={leftColSize} display={{ xs: 'none', md: 'block' }}>
              <Stack spacing={DASHBOARD_GRID_SPACING_REM}>
                <Collapse in={hasMapCard} unmountOnExit>
                  <Box
                    sx={{
                      height: mapCardHeight > 0 ? `${mapCardHeight}px` : 'auto',
                      width: '100%',
                    }}
                  >
                    <MapCard width={mapCardHeight} height={mapCardHeight} />
                  </Box>
                </Collapse>

                <Collapse in={!!features['crop_info_card']} unmountOnExit>
                  <Box height={remainingCardHeight}>
                    <CropInfoCard />
                  </Box>
                </Collapse>

                <Collapse in={!!features['task_list_card'] && !features['crop_info_card']} unmountOnExit>
                  <Box height={remainingCardHeight}>
                    <TaskListCard />
                  </Box>
                </Collapse>
              </Stack>
            </Grid>
          )}

          {hasRightColumn && (
            <Grid size={rightColSize}>
              <Stack
                direction="column"
                spacing={DASHBOARD_GRID_SPACING_REM}
                sx={{ height: { xs: FULL_CONTENT_HEIGHT_XS, md: FULL_CONTENT_HEIGHT_MD } }}
              >
                <Collapse in={hasTimelineCard} sx={{ flexShrink: 0 }}>
                  <Box sx={{ height: timelineHeight }}>
                    <CropScheduleCard />
                  </Box>
                </Collapse>

                <Collapse
                  in={hasMetricsCard}
                  sx={{ flexShrink: 0, display: { xs: 'none', md: 'block' } }}
                >
                  <Box sx={{ height: metricsHeight }}>
                    <MetricsCard />
                  </Box>
                </Collapse>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
    </MapRefreshProvider>
  )
}

export default Dashboard
